import React, { useEffect, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { CheckCircle2 } from "lucide-react";
import { useLocalization } from "@i18n";

interface ModuleCardProps {
  icon: LucideIcon;
  name: string;
  description: string;
  progress: number;
  isActive: boolean;
  isCompleted?: boolean;
  onClick?: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  icon: Icon,
  name,
  description,
  progress,
  isActive,
  isCompleted = false,
  onClick,
}) => {
  const { translate } = useLocalization();
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const iconBg = isCompleted
    ? "bg-(--success)/10 text-success"
    : isActive
      ? "bg-primary/10 text-primary"
      : "bg-muted text-muted-foreground";

  const borderClass = isCompleted
    ? "border-(--success)  shadow-md hover:shadow-lg"
    : isActive
      ? "border-primary shadow-md hover:shadow-lg"
      : "border-border-1 hover:border-border-2";

  const badgeClass = isCompleted
    ? "bg-success/10 text-success"
    : isActive
      ? "bg-primary/10 text-primary"
      : "bg-muted text-muted-foreground";

  const badgeLabel = isCompleted
    ? translate("home.modules.badgeCompleted")
    : isActive
      ? translate("home.modules.badgeActive")
      : translate("home.modules.badgeSoon");

  const barClass = isCompleted
    ? "bg-success"
    : isActive
      ? "bg-primary"
      : "bg-muted-foreground/30";

  const progressTextClass = isCompleted
    ? "text-success"
    : isActive
      ? "text-primary"
      : "text-muted-foreground";

  return (
    <div
      className={`flex flex-col gap-3 p-5 rounded-xl border bg-background shadow-sm transition-all duration-200 ${borderClass} ${
        onClick ? "cursor-pointer" : ""
      }`}
      onClick={onClick}
    >
      {/* Icon + Badge */}
      <div className="flex items-center justify-between">
        <div className={`p-2 rounded-lg ${iconBg}`}>
          <Icon size={20} />
        </div>
        <span
          className={`flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass} gap-2`}
        >
          {badgeLabel} {isCompleted && <CheckCircle2 size={20} />}
        </span>
      </div>

      {/* Name + Description */}
      <div>
        <p className="text-body-default font-semibold text-foreground leading-tight">
          {name}
        </p>
        <p className="text-body-small text-muted-foreground mt-0.5 leading-snug">
          {description}
        </p>
      </div>

      {/* Progress bar */}
      <div className="mt-auto">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-muted-foreground">
            {translate("home.modules.progress")}
          </span>
          <span className={`text-xs font-semibold ${progressTextClass}`}>
            {progress}%
          </span>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-1000 ease-out ${barClass}`}
            style={{ width: animate ? `${progress}%` : "0%" }}
          />
        </div>
      </div>
    </div>
  );
};

export default ModuleCard;
